import { statusMeta, statusCode, minutesLabel, PENDING_META } from "../../utils/attendance";
import { formatDateShort } from "../../utils/formatters";

/**
 * One status-coloured cell, in two densities:
 *  - compact: a small square for the muster grid (colour + code, late dot,
 *    missing-checkout marker)
 *  - detailed: a calendar cell (day number, status name / worked duration /
 *    first-in time, holiday or leave-type name when known)
 * `day` is a day entry from the API (or null/undefined = no data).
 *
 * "Today" is live and in-progress, so it never renders a final red "Absent":
 * if the person has punched in it reads Present (in progress); if not, a
 * neutral "Not in yet". The day-end sweep sets the real status. An admin
 * override still wins.
 */
export default function AttendanceDayCell({ day, compact = false, today = false, onClick }) {
  const liveAbsent = today && day?.status === "absent" && !day?.is_overridden;
  const checkedInToday =
    liveAbsent && (day.worked_minutes > 0 || day.missing_checkout || !!day.first_in_at);
  const pendingToday = liveAbsent && !checkedInToday;

  const meta = checkedInToday
    ? statusMeta("full")
    : pendingToday
      ? PENDING_META
      : day?.status
        ? statusMeta(day.status)
        : null;
  const code = checkedInToday ? "P" : pendingToday ? PENDING_META.code : statusCode(day);
  // An open slot today means "currently checked in", not a missed checkout.
  const showMissing = day?.missing_checkout && !today;
  const title = cellTitle(day, { checkedInToday, pendingToday });

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        disabled={!onClick}
        style={{
          width: 30,
          height: 30,
          borderRadius: 6,
          border: today ? "2px solid var(--primary-600, #4f46e5)" : "1px solid var(--line, #e2e8f0)",
          background: meta ? meta.soft : "transparent",
          color: meta ? meta.color : "var(--ink-400, #94a3b8)",
          fontSize: 10,
          fontWeight: 700,
          cursor: onClick ? "pointer" : "default",
          position: "relative",
          padding: 0,
        }}
      >
        {code}
        {day?.late && (
          <span style={{ position: "absolute", top: 1, right: 3, color: "#f59e0b", fontSize: 10, lineHeight: 1 }}>•</span>
        )}
        {showMissing && (
          <span style={{ position: "absolute", bottom: 0, right: 2, color: "#ef4444", fontSize: 8, lineHeight: 1.4 }}>⚠</span>
        )}
      </button>
    );
  }

  const dayNum = day?.work_date ? Number(day.work_date.slice(8, 10)) : "";

  return (
    <div
      className={`cal-cell ${today ? "today" : ""}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        background: meta ? meta.soft : undefined,
        minHeight: 74,
      }}
      title={title}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="cal-date">{dayNum}</span>
        <span className="row gap-sm" style={{ alignItems: "center" }}>
          {day?.late && <span style={{ color: "#f59e0b", fontWeight: 700, fontSize: 12 }}>•</span>}
          {showMissing && <span style={{ color: "#ef4444", fontSize: 10 }}>⚠</span>}
          {meta && (
            <span style={{ color: meta.color, fontSize: 10, fontWeight: 700 }}>{code}</span>
          )}
        </span>
      </div>
      {meta && (
        <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: meta.color }}>
          {checkedInToday
            ? "Present (in progress)"
            : day.status === "leave" && Number(day.leave_days) === 0.5
              ? "½ Leave"
              : meta.label}
          {day.holiday_name ? ` · ${day.holiday_name}` : ""}
        </div>
      )}
      {day?.first_in_at && (
        <div className="t-muted" style={{ fontSize: 10, marginTop: 2 }}>
          In {timeOf(day.first_in_at)}
          {day.last_out_at ? ` · Out ${timeOf(day.last_out_at)}` : ""}
        </div>
      )}
      {day?.worked_minutes > 0 && (
        <div className="t-muted" style={{ fontSize: 10, marginTop: 1 }}>
          {minutesLabel(day.worked_minutes)} worked
        </div>
      )}
    </div>
  );
}

function timeOf(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
}

function cellTitle(day, { checkedInToday = false, pendingToday = false } = {}) {
  if (!day?.work_date) return "";
  const date = formatDateShort(day.work_date);
  if (pendingToday) return `${date} · Not checked in yet`;
  if (checkedInToday) {
    const bits = [date, "Present (in progress)"];
    if (day.late) bits.push(`late ${day.late_minutes}m`);
    if (day.worked_minutes > 0) bits.push(`${minutesLabel(day.worked_minutes)} worked`);
    return bits.join(" · ");
  }
  if (!day.status) return date;
  const bits = [date, statusMeta(day.status)?.label];
  if (day.late) bits.push(`late ${day.late_minutes}m`);
  if (day.missing_checkout) bits.push("missing checkout");
  if (day.worked_minutes > 0) bits.push(`${minutesLabel(day.worked_minutes)} worked`);
  return bits.filter(Boolean).join(" · ");
}
